import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  eventId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    eventId: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, eventId: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    const id = Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    this.setState({ eventId: id });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div
          className='min-h-screen flex flex-col items-center justify-center px-6 text-center'
          style={{ background: '#08080f', color: '#fff' }}
        >
          <div
            className='w-full max-w-sm rounded-[20px] p-7'
            style={{ background: '#0d0d1a', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            {/* Logo mark */}
            <div
              className='w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4'
              style={{ background: 'linear-gradient(135deg,#2dd4bf,#34d399)' }}
            >
              <svg
                width='22'
                height='22'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#08080f'
                strokeWidth='2.5'
                strokeLinecap='round'
              >
                <path d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' />
                <polyline points='9 22 9 12 15 12 15 22' />
              </svg>
            </div>

            <h2 className='text-[18px] font-extrabold text-white mb-2'>Something went wrong</h2>
            <p
              className='text-[13px] leading-relaxed mb-5'
              style={{ color: 'var(--color-text-dim)' }}
            >
              An unexpected error occurred. Reloading usually fixes this — if it keeps happening,
              let us know.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <p
                className='text-[10px] font-mono mb-5 px-3 py-2 rounded-[10px] text-left break-all'
                style={{
                  background: 'rgba(239,68,68,0.07)',
                  border: '1px solid rgba(239,68,68,0.18)',
                  color: '#fca5a5',
                }}
              >
                {this.state.error.message}
              </p>
            )}

            <div className='flex flex-col gap-2.5'>
              <button
                onClick={() => window.location.reload()}
                className='w-full py-2.5 rounded-[12px] text-[13px] font-bold text-base'
                style={{ background: 'linear-gradient(135deg,#2dd4bf,#34d399)' }}
              >
                Reload Page
              </button>
              <a
                href='/'
                className='w-full py-2.5 rounded-[12px] text-[13px] font-semibold text-center block'
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'var(--color-text-muted)',
                }}
              >
                Go to Home
              </a>
              {this.state.eventId && (
                <p className='text-[10px] mt-1' style={{ color: 'var(--color-text-dim)' }}>
                  Error ID: <span className='font-mono'>{this.state.eventId}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
